import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  uploadPatientFile,
  createPatientFile,
  getPatientFiles,
  getTreatmentFiles,
  updatePatientFile,
  deletePatientFile,
  createPatientFolder,
  getPatientFolders,
  updatePatientFolder,
  deletePatientFolder,
  getClinicStorageUsage,
} from "../../firebase";

export const patientFilesApi = createApi({
  reducerPath: "patientFilesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["PatientFile", "PatientFolder", "StorageUsage"],
  endpoints: (builder) => ({
    // ============================================
    // FILES
    // ============================================

    // Get patient files (for patient file tab)
    getPatientFiles: builder.query({
      async queryFn({ clinicId, patientId, therapistId, folderId }) {
        try {
          const files = await getPatientFiles(clinicId, patientId, therapistId, {
            folderId,
          });
          return { data: files };
        } catch (error) {
          console.error("[CK-7900] Error fetching patient files:", {
            errorCode: "CK-7900",
            errorMessage: error.message,
            clinicId,
            patientId,
            therapistId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PatientFile", id })),
              { type: "PatientFile", id: `LIST-${patientId}` },
            ]
          : [{ type: "PatientFile", id: `LIST-${patientId}` }],
    }),

    // Get treatment files
    getTreatmentFiles: builder.query({
      async queryFn({ clinicId, patientId, treatmentId }) {
        try {
          const files = await getTreatmentFiles(clinicId, patientId, treatmentId);
          return { data: files };
        } catch (error) {
          console.error("[CK-7901] Error fetching treatment files:", {
            errorCode: "CK-7901",
            errorMessage: error.message,
            clinicId,
            patientId,
            treatmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { treatmentId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PatientFile", id })),
              { type: "PatientFile", id: `TREATMENT-${treatmentId}` },
            ]
          : [{ type: "PatientFile", id: `TREATMENT-${treatmentId}` }],
    }),

    // Upload and create patient file
    uploadPatientFile: builder.mutation({
      async queryFn({
        file,
        clinicId,
        patientId,
        therapistId,
        therapistName,
        folderId,
        description,
        treatmentId,
        linkedToTreatmentOnly,
      }) {
        try {
          // First upload the file to storage
          const uploadResult = await uploadPatientFile(
            file,
            clinicId,
            patientId,
            therapistId
          );

          // Then create the file record
          const fileData = {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            fileUrl: uploadResult.url,
            storagePath: uploadResult.storagePath,
            folderId: folderId || null,
            description: description || null,
            treatmentId: treatmentId || null,
            linkedToTreatmentOnly: linkedToTreatmentOnly || false,
          };

          const fileRecord = await createPatientFile(
            clinicId,
            patientId,
            fileData,
            therapistId,
            therapistName
          );

          return { data: fileRecord };
        } catch (error) {
          console.error("[CK-7902] Error uploading patient file:", {
            errorCode: "CK-7902",
            errorMessage: error.message,
            clinicId,
            patientId,
            fileName: file?.name,
            fileSize: file?.size,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, treatmentId }) => [
        { type: "PatientFile", id: `LIST-${patientId}` },
        ...(treatmentId
          ? [{ type: "PatientFile", id: `TREATMENT-${treatmentId}` }]
          : []),
        { type: "StorageUsage", id: "USAGE" },
      ],
    }),

    // Update patient file (description, folder)
    updatePatientFile: builder.mutation({
      async queryFn({ clinicId, patientId, fileId, fileData }) {
        try {
          const file = await updatePatientFile(
            clinicId,
            patientId,
            fileId,
            fileData
          );
          return { data: file };
        } catch (error) {
          console.error("[CK-7903] Error updating patient file:", {
            errorCode: "CK-7903",
            errorMessage: error.message,
            clinicId,
            patientId,
            fileId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, fileId }) => [
        { type: "PatientFile", id: fileId },
        { type: "PatientFile", id: `LIST-${patientId}` },
      ],
    }),

    // Delete patient file
    deletePatientFile: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        fileId,
        therapistId,
        therapistName,
      }) {
        try {
          await deletePatientFile(
            clinicId,
            patientId,
            fileId,
            therapistId,
            therapistName
          );
          return { data: fileId };
        } catch (error) {
          console.error("[CK-7904] Error deleting patient file:", {
            errorCode: "CK-7904",
            errorMessage: error.message,
            clinicId,
            patientId,
            fileId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PatientFile", id: `LIST-${patientId}` },
        { type: "StorageUsage", id: "USAGE" },
      ],
    }),

    // ============================================
    // FOLDERS
    // ============================================

    // Get patient folders
    getPatientFolders: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const folders = await getPatientFolders(clinicId, patientId);
          return { data: folders };
        } catch (error) {
          console.error("[CK-7905] Error fetching patient folders:", {
            errorCode: "CK-7905",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PatientFolder", id })),
              { type: "PatientFolder", id: `LIST-${patientId}` },
            ]
          : [{ type: "PatientFolder", id: `LIST-${patientId}` }],
    }),

    // Create patient folder
    createPatientFolder: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        folderData,
        therapistId,
        therapistName,
      }) {
        try {
          const folder = await createPatientFolder(
            clinicId,
            patientId,
            folderData,
            therapistId,
            therapistName
          );
          return { data: folder };
        } catch (error) {
          console.error("[CK-7906] Error creating patient folder:", {
            errorCode: "CK-7906",
            errorMessage: error.message,
            clinicId,
            patientId,
            folderData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PatientFolder", id: `LIST-${patientId}` },
      ],
    }),

    // Update patient folder
    updatePatientFolder: builder.mutation({
      async queryFn({ clinicId, patientId, folderId, folderData }) {
        try {
          const folder = await updatePatientFolder(
            clinicId,
            patientId,
            folderId,
            folderData
          );
          return { data: folder };
        } catch (error) {
          console.error("[CK-7907] Error updating patient folder:", {
            errorCode: "CK-7907",
            errorMessage: error.message,
            clinicId,
            patientId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId, folderId }) => [
        { type: "PatientFolder", id: folderId },
        { type: "PatientFolder", id: `LIST-${patientId}` },
      ],
    }),

    // Delete patient folder
    deletePatientFolder: builder.mutation({
      async queryFn({ clinicId, patientId, folderId }) {
        try {
          await deletePatientFolder(clinicId, patientId, folderId);
          return { data: folderId };
        } catch (error) {
          console.error("[CK-7908] Error deleting patient folder:", {
            errorCode: "CK-7908",
            errorMessage: error.message,
            clinicId,
            patientId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PatientFolder", id: `LIST-${patientId}` },
      ],
    }),

    // ============================================
    // STORAGE
    // ============================================

    // Get clinic storage usage
    getStorageUsage: builder.query({
      async queryFn({ clinicId }) {
        try {
          const usage = await getClinicStorageUsage(clinicId);
          return { data: usage };
        } catch (error) {
          console.error("[CK-7909] Error fetching storage usage:", {
            errorCode: "CK-7909",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "StorageUsage", id: "USAGE" }],
    }),
  }),
});

export const {
  // Files
  useGetPatientFilesQuery,
  useGetTreatmentFilesQuery,
  useUploadPatientFileMutation,
  useUpdatePatientFileMutation,
  useDeletePatientFileMutation,
  // Folders
  useGetPatientFoldersQuery,
  useCreatePatientFolderMutation,
  useUpdatePatientFolderMutation,
  useDeletePatientFolderMutation,
  // Storage
  useGetStorageUsageQuery,
} = patientFilesApi;

